'use client';

// The speech notice above the composer (replaces VoiceErrorNotice).
//
// - kept:           the error, that the recording is kept, and Retry / Discard
// - pending-insert: the committed text is ready: Insert / Discard
// - otherwise:      a dismissible error (nothing was kept)
// Every action is a 44 px target; the notice is a polite status region. A
// dictation refused for used-up company usage credit (402) also opens billing,
// as a chat question does (#2841); the audio is kept for Try again.
import { useBilling } from '@/billing/context';
import { Button } from '@/components/ui/button';
import { useChatCopy } from '@/lib/chat-i18n';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import speechCopy, { type SpeechCopyKey } from '@/i18n/ns/speech';
import { RotateCcwIcon, Trash2Icon, XIcon, CheckIcon } from 'lucide-react';
import { useEffect, useRef, type FC, type ReactNode } from 'react';
import type { SpeechIssue } from '../errors';
import { useSpeech, useSpeechState } from './speech-provider';

/** Copy that already says the recording is kept on the device. */
const SAYS_KEPT: ReadonlySet<SpeechCopyKey> = new Set([
  'keptForRetry',
  'offline',
  'timeout',
  'tooLong',
  'replaced',
]);

type Copy = Readonly<Record<SpeechCopyKey, string>>;

const issueText = (copy: Copy, issue: SpeechIssue) =>
  formatCopy(copy[issue.key], issue.values ?? {});

/** The notice's lines for a state. Exported for tests. */
export function speechNoticeLines(
  copy: Copy,
  state: { phase: string; error: SpeechIssue | null },
): string[] {
  const { phase, error } = state;
  if (phase === 'pending-insert')
    return [...(error ? [issueText(copy, error)] : []), copy.pendingInsert];
  if (phase === 'kept') {
    const lines = error ? [issueText(copy, error)] : [];
    return error && SAYS_KEPT.has(error.key) ? lines : [...lines, copy.keptForRetry];
  }
  return error ? [issueText(copy, error)] : [];
}

const Action: FC<{
  slot: string;
  label: string;
  icon: ReactNode;
  primary?: boolean;
  onClick(): void;
}> = ({ slot, label, icon, primary, onClick }) => (
  <Button
    type="button"
    variant={primary ? 'default' : 'ghost'}
    data-slot={slot}
    className="h-11 min-w-11 shrink-0 gap-1.5 rounded-full px-3"
    onClick={onClick}
  >
    {icon}
    <span>{label}</span>
  </Button>
);

export const SpeechNotice: FC = () => {
  const speech = useSpeech();
  const copy = useNamespaceCopy(speechCopy);
  const chatCopy = useChatCopy();
  const phase = useSpeechState((state) => state.phase);
  const error = useSpeechState((state) => state.error);
  const billingState = useBilling();
  const billing = useRef(billingState);
  billing.current = billingState;
  // Once per refusal: the billing state object changes on every refresh.
  useEffect(() => {
    if (error?.code === 'payment_required') billing.current?.request();
  }, [error]);
  if (!speech) return null;
  const lines = speechNoticeLines(copy, { phase, error });
  if (lines.length === 0) return null;
  const controller = speech.controller;
  const kept = phase === 'kept';
  const pending = phase === 'pending-insert';
  const problem = Boolean(error) && !pending;
  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-slot="poneva-speech-notice"
      data-phase={phase}
      className={
        problem
          ? 'border-destructive/40 bg-destructive/8 text-destructive mx-2 flex min-h-11 flex-wrap items-center gap-2 rounded-xl border px-3 py-2 text-sm'
          : 'border-border/70 bg-muted/50 text-foreground mx-2 flex min-h-11 flex-wrap items-center gap-2 rounded-xl border px-3 py-2 text-sm'
      }
    >
      <span className="min-w-0 flex-1 basis-48">
        {lines.map((line) => (
          <span key={line} className="block">
            {line}
          </span>
        ))}
      </span>
      {pending && (
        <Action
          slot="speech-insert-pending"
          primary
          label={copy.insert}
          icon={<CheckIcon className="size-4" aria-hidden="true" />}
          onClick={() => controller.insertPending()}
        />
      )}
      {kept && (
        <Action
          slot="speech-retry"
          primary
          label={copy.retry}
          icon={<RotateCcwIcon className="size-4" aria-hidden="true" />}
          onClick={() => void controller.retry()}
        />
      )}
      {(kept || pending) && (
        <Action
          slot="speech-discard"
          label={copy.discard}
          icon={<Trash2Icon className="size-4" aria-hidden="true" />}
          onClick={() => void controller.discard()}
        />
      )}
      {!kept && !pending && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          data-slot="speech-dismiss"
          className="size-11 rounded-full"
          onClick={() => controller.dismiss()}
          aria-label={chatCopy.close}
        >
          <XIcon className="size-4" />
        </Button>
      )}
    </div>
  );
};
